import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Building2, User, Plus, Trash2, Star, StarOff, Save, 
  Loader2, Phone, Mail, Globe, MapPin, Scale, ArrowLeft
} from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';

export default function FirmProfilePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [firm, setFirm] = useState({
    firm_name: '',
    phone: '',
    fax: '',
    email: '',
    website: '',
    address: {
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: 'CA',
      zip: ''
    }
  });
  
  const [attorneys, setAttorneys] = useState([]);
  const [newAttorney, setNewAttorney] = useState({
    full_name: '',
    email: '',
    phone: '',
    bar_number: '',
    licensing_authority: 'State Bar of California',
    is_default: false
  });
  const [showAddAttorney, setShowAddAttorney] = useState(false);
  const [stateOptions, setStateOptions] = useState([]);

  useEffect(() => {
    const initData = async () => {
      await fetchUser();
      await fetchFirmData();
      await fetchStateOptions();
    };
    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        // Check CRM access
        if (userData.app_mode !== 'crm') {
          toast.error('CRM features require CRM subscription');
          navigate('/dashboard');
        }
      } else {
        navigate('/auth');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchFirmData = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/crm/firm`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.firm) {
          setFirm({
            ...data.firm,
            address: data.firm.address || {
              address_line_1: '',
              address_line_2: '',
              city: '',
              state: 'California',
              zip: ''
            }
          });
        }
        setAttorneys(data.attorneys || []);
      }
    } catch (error) {
      console.error('Error fetching firm data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStateOptions = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/global-fields/option-lists`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const usaStatesList = data.system_lists.find(list => list.name === 'UsaStates');
        if (usaStatesList) {
          setStateOptions(usaStatesList.options);
        }
      }
    } catch (error) {
      console.error('Failed to load state options');
    }
  };


  const handleSaveFirm = async () => {
    if (!firm.firm_name.trim()) {
      toast.error('Firm name is required');
      return;
    }
    
    setSaving(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API}/crm/firm`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(firm)
      });
      
      if (response.ok) {
        toast.success('Firm profile saved');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save firm profile');
      }
    } catch (error) {
      toast.error('Failed to save firm profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAttorney = async () => {
    if (!newAttorney.full_name.trim()) {
      toast.error('Attorney name is required');
      return;
    }
    
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API}/crm/attorneys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAttorney)
      });
      
      if (response.ok) {
        toast.success('Attorney added');
        setNewAttorney({
          full_name: '',
          email: '',
          phone: '',
          bar_number: '',
          licensing_authority: 'State Bar of California',
          is_default: false
        });
        setShowAddAttorney(false);
        fetchFirmData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add attorney');
      }
    } catch (error) {
      toast.error('Failed to add attorney');
    }
  };

  const handleSetDefault = async (attorneyId) => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API}/crm/attorneys/${attorneyId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_default: true })
      });
      
      if (response.ok) {
        toast.success('Default attorney updated');
        fetchFirmData();
      }
    } catch (error) {
      toast.error('Failed to update attorney');
    }
  };

  const handleDeleteAttorney = async (attorneyId) => {
    if (!confirm('Are you sure you want to delete this attorney?')) return;
    
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API}/crm/attorneys/${attorneyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Attorney deleted');
        fetchFirmData();
      }
    } catch (error) {
      toast.error('Failed to delete attorney');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col lg:ml-64">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-5xl mx-auto px-6 py-8 lg:pt-8 pt-20">{/* pt-20 for mobile header */}
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="rounded-xl"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Firm Profile</h1>
              <p className="text-slate-500">Manage your firm information and attorneys</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Firm Information */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-violet-600" />
              </div>
              <h2 className="text-lg font-semibold">Firm Information</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-slate-600">Firm Name *</Label>
                <Input
                  value={firm.firm_name}
                  onChange={(e) => setFirm({ ...firm, firm_name: e.target.value })}
                  placeholder="Your Law Firm Name"
                  className="mt-1"
                  data-testid="firm-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-600">Phone</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={firm.phone}
                      onChange={(e) => setFirm({ ...firm, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Fax</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={firm.fax}
                      onChange={(e) => setFirm({ ...firm, fax: e.target.value })}
                      placeholder="(555) 123-4568"
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm text-slate-600">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={firm.email}
                      onChange={(e) => setFirm({ ...firm, email: e.target.value })}
                      placeholder="contact@yourfirm.com"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <Label className="text-sm text-slate-600">Website</Label>
                <div className="relative mt-1">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={firm.website}
                    onChange={(e) => setFirm({ ...firm, website: e.target.value })}
                    placeholder="https://www.yourfirm.com"
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="border-t border-slate-100 pt-4 mt-4">
                <Label className="text-sm text-slate-600 flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4" />
                  Address
                </Label>
                
                <div className="space-y-3">
                  <Input
                    value={firm.address.address_line_1}
                    onChange={(e) => setFirm({ 
                      ...firm, 
                      address: { ...firm.address, address_line_1: e.target.value }
                    })}
                    placeholder="Address Line 1"
                  />
                  <Input
                    value={firm.address.address_line_2}
                    onChange={(e) => setFirm({ 
                      ...firm, 
                      address: { ...firm.address, address_line_2: e.target.value }
                    })}
                    placeholder="Address Line 2 (Suite, Unit, etc.)"
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      value={firm.address.city}
                      onChange={(e) => setFirm({ 
                        ...firm, 
                        address: { ...firm.address, city: e.target.value }
                      })}
                      placeholder="City"
                    />
                    <select
                      value={firm.address.state}
                      onChange={(e) => setFirm({ 
                        ...firm, 
                        address: { ...firm.address, state: e.target.value }
                      })}
                      className="h-11 rounded-lg border border-slate-200 px-3 text-sm"
                    >
                      <option value="">Select State</option>
                      {stateOptions.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                    <Input
                      value={firm.address.zip}
                      onChange={(e) => setFirm({ 
                        ...firm, 
                        address: { ...firm.address, zip: e.target.value }
                      })}
                      placeholder="ZIP"
                    />
                  </div>
                </div>
              </div>
              
              <Button
                onClick={handleSaveFirm}
                disabled={saving}
                className="w-full mt-4 bg-violet-600 hover:bg-violet-700 text-white rounded-xl h-11"
                data-testid="save-firm-btn"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" /> Save Firm Profile</>
                )}
              </Button>
            </div>
          </div>

          {/* Attorneys */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Scale className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Attorneys</h2>
                  <p className="text-sm text-slate-500">{attorneys.length} attorney{attorneys.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <Button
                onClick={() => setShowAddAttorney(true)}
                variant="outline"
                className="rounded-xl"
                data-testid="add-attorney-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Attorney
              </Button>
            </div>

            {/* Add Attorney Form */}
            {showAddAttorney && (
              <div className="mb-6 p-4 rounded-xl bg-slate-50 border border-slate-200">
                <h3 className="font-medium mb-3">New Attorney</h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm text-slate-600">Full Name *</Label>
                    <Input
                      value={newAttorney.full_name}
                      onChange={(e) => setNewAttorney({ ...newAttorney, full_name: e.target.value })}
                      placeholder="John Smith"
                      className="mt-1"
                      data-testid="new-attorney-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm text-slate-600">Email</Label>
                      <Input
                        value={newAttorney.email}
                        onChange={(e) => setNewAttorney({ ...newAttorney, email: e.target.value })}
                        placeholder="john@firm.com"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Phone</Label>
                      <Input
                        value={newAttorney.phone}
                        onChange={(e) => setNewAttorney({ ...newAttorney, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm text-slate-600">Bar Number</Label>
                      <Input
                        value={newAttorney.bar_number}
                        onChange={(e) => setNewAttorney({ ...newAttorney, bar_number: e.target.value })}
                        placeholder="123456"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Licensing Authority</Label>
                      <Input
                        value={newAttorney.licensing_authority}
                        onChange={(e) => setNewAttorney({ ...newAttorney, licensing_authority: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      onClick={handleAddAttorney}
                      className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl"
                      data-testid="confirm-add-attorney"
                    >
                      Add Attorney
                    </Button>
                    <Button
                      onClick={() => setShowAddAttorney(false)}
                      variant="outline"
                      className="rounded-xl"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Attorney List */}
            <div className="space-y-3">
              {attorneys.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <User className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No attorneys added yet</p>
                  <p className="text-sm">Add at least one attorney to use for document prefills</p>
                </div>
              ) : (
                attorneys.map((attorney) => (
                  <div
                    key={attorney.attorney_id}
                    className={`p-4 rounded-xl border ${
                      attorney.is_default 
                        ? 'border-amber-200 bg-amber-50' 
                        : 'border-slate-200 bg-white'
                    }`}
                    data-testid={`attorney-${attorney.attorney_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{attorney.full_name}</span>
                          {attorney.is_default && (
                            <span className="px-2 py-0.5 rounded-full bg-amber-200 text-amber-700 text-xs font-medium">
                              Default
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-500 mt-1 space-y-0.5">
                          {attorney.bar_number && (
                            <p>Bar #{attorney.bar_number} • {attorney.licensing_authority}</p>
                          )}
                          {attorney.email && <p>{attorney.email}</p>}
                          {attorney.phone && <p>{attorney.phone}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {!attorney.is_default && (
                          <Button
                            onClick={() => handleSetDefault(attorney.attorney_id)}
                            variant="ghost"
                            size="sm"
                            className="rounded-lg text-slate-500 hover:text-amber-600"
                            title="Set as default"
                          >
                            <StarOff className="w-4 h-4" />
                          </Button>
                        )}
                        {attorney.is_default && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-lg text-amber-500"
                            disabled
                          >
                            <Star className="w-4 h-4 fill-current" />
                          </Button>
                        )}
                        <Button
                          onClick={() => handleDeleteAttorney(attorney.attorney_id)}
                          variant="ghost"
                          size="sm"
                          className="rounded-lg text-slate-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
}
